import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import "../ViewInvoice.css";
import ErrorPopup from "../../../../components/element/ErrorPopup";
import Loader from "../../../../components/element/Loader";
import NoDataPlaceholder from "../../../../components/element/NoDataPlaceholder";
import {
  formatToTwoDecimal,
  getAirportData,
  handleAircraftType,
} from "../../../../utils/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { StorageContext } from "../../../../services/StorageProvider";
import { DATE_FORMAT, TIME_FORMAT } from "../../../../utils/constants";
import useFBOApi from "../../../../services/api/useFBOApi";

dayjs.extend(utc);

const AirlineNameCell = ({ airlineId }) => {
  const { getAirlineById } = useFBOApi();

  const { data: airlineData, isLoading: isAirlineLoading } = useQuery({
    queryKey: ["airline", airlineId],
    queryFn: () => getAirlineById(airlineId),
    enabled: !!airlineId,
    retry: false,
  });

  if (isAirlineLoading) {
    return <td>Loading...</td>;
  }

  return <td>{airlineData?.data?.airlineName ?? "---"}</td>;
};

const ViewLandingFeeInvoiceTable = ({
  invoiceData,
  isLoading,
  error,
  isCommercialFlight = false,
}) => {
  const { airportListData } = useContext(StorageContext);

  if (error) {
    if (error.status === 404) {
      return <NoDataPlaceholder />;
    } else {
      return <ErrorPopup children={error.message}></ErrorPopup>;
    }
  }

  if (isLoading) {
    return <Loader />;
  }

  if (invoiceData && invoiceData.length === 0) {
    return <NoDataPlaceholder />;
  }

  return (
    <div className="view-invoice-table-parent-container">
      <div className="view-invoice-table-container">
        <table className="view-invoice-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Aircraft Registration</th>
              <th>Aircraft Type</th>
              <th>MTOW (lbs)</th>
              {isCommercialFlight ? (
                <>
                  <th>Flight Number</th>
                  <th>Airline Name</th>
                </>
              ) : (
                <th>Owner Name</th>
              )}
              <th>Departure Airport</th>
              <th>Arrival Airport</th>
              <th>Arrival Date</th>
              <th>Arrival Time</th>
              <th>VAT (10%)</th>
              <th>Landing Fee Collected</th>
            </tr>
          </thead>
          <tbody>
            {invoiceData?.map((item, index) => (
              <React.Fragment key={index}>
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item?.aircraftRegistrationNumber}</td>
                  <td>{handleAircraftType(item?.engineType)}</td>
                  <td>{item?.maxTakeOffWeight}</td>
                  {isCommercialFlight ? (
                    <>
                      <td>{item?.flightNumber ?? "---"}</td>
                      <AirlineNameCell airlineId={item?.airlineId} />
                    </>
                  ) : (
                    <td>{item?.aircraftOwnerName}</td>
                  )}
                  <td className="departure-airport-cell">
                    {
                      getAirportData(airportListData, item?.departureAirportId)
                        ?.name
                    }
                  </td>
                  <td>
                    {
                      getAirportData(airportListData, item?.arrivalAirportId)
                        ?.name
                    }
                  </td>
                  <td>
                    {dayjs(item?.arrivalTime).local().format(DATE_FORMAT)}
                  </td>
                  <td>{dayjs(item?.arrivalTime).local().format(TIME_FORMAT)}</td>
                  <td>{formatToTwoDecimal(item?.tax)}</td>
                  <td>{`BSD ${formatToTwoDecimal(
                    item?.landingFeeCollected
                  )}`}</td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViewLandingFeeInvoiceTable;
